/**
 * "Midcall sagas" handle performing local mid-call operations.
 *
 * These sagas assume that there is an established session (webRTC and server-
 *    side) to perform the operation on. Otherwise, it is considered an error.
 */

// Callstack plugin.
import { setupCall } from '../webrtc/establish'
import { generateOffer, closeCall, webRtcRemoveMedia, webRtcAddMedia, webRtcReplaceTrack } from '../webrtc/midcall'
import * as rollbackOps from '../webrtc/rollback'
import { checkBandwidthControls } from '../utils/bandwidth'

// Call plugin.
import { callActions } from '../../call/interfaceNew/actions'
import { getCallById, getOptions, getTurnInfo } from '../../call/interfaceNew/selectors'

// Other plugins.
import { logManager } from '../../logs'
import BasicError, { callCodes, subscriptionCodes } from '../../errors'
import { getTrackById, getSessionById } from '../../webrtc/interface/selectors'
import { getPlatform } from '../../auth/interface/selectors'
import { getConnectionState } from '../../connectivity/interface/selectors'
import { waitForReconnect } from '../../connectivity/interface/effects'

// Helpers.
import { CALL_STATES, CALL_DIRECTION } from '../../call/constants'

// Libraries.
import { all, call, put, select, delay } from 'redux-saga/effects'
import { isArray, isEmpty } from 'lodash/fp'

// Utils
import { getVersion } from '../../common/version'
import { platforms } from '../../constants'
import sdkId from '../../common/sdkId'

/**
 * This saga ends a call, it performs the webRTC and signaling operations to
 *    update the local and server sessions of a call.
 * Assumptions:
 *    1. None.
 * Responsibilities:
 *    1. Perform the signaling to tell the server that we want to end the session
 *    2. Update call state (via redux actions).
 * @method endCall
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.endSession "End session" signalling function.
 * @param {Object}   action An action of type `END_CALL`.
 */
export function * endCall (deps, action) {
  const { webRTC, requests } = deps
  const { id } = action.payload

  const log = logManager.getLogger('CALL', id)
  log.info('Ending call.')

  const targetCall = yield select(getCallById, id)
  if (!targetCall) {
    log.debug('Call not found: Invalid call ID.')
    yield put(
      callActions.endCallFinish(id, {
        isLocal: true,
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call state not found; invalid call ID.'
        })
      })
    )
    return
  }

  const { wrtcsSessionId, webrtcSessionId, isAnonymous, account } = targetCall

  // Perform webRTC functions
  yield call(closeCall, webRTC, webrtcSessionId)

  // Perform signalling to end the session
  const response = yield call(requests.endSession, { wrtcsSessionId, isAnonymous, account })

  log.info(`Finished ending call. Changing to ${CALL_STATES.ENDED}.`)
  if (!response.error) {
    yield put(callActions.endCallFinish(id, { isLocal: true }))
  } else {
    yield put(
      callActions.endCallFinish(id, {
        isLocal: true,
        error: response.error
      })
    )
  }
}

/**
 * Updates an existing call to have inactive media.
 * Can be used as a "hold" operation for plain webRTC scenarios.
 *
 * This saga defines how a call is put "on hold". It performs the webRTC and
 *    signaling operations to update the local and server sessions of a call.
 *    This represents the start of the webRTC renegotiation process for the
 *    local side.
 * Assumptions:
 *    1. The call is in the correct state for the operation.
 *        - The call exists, and is not already locally held.
 * Responsibilities:
 *    1. Generate an offer with the media directions as "locally held".
 *    2. Perform signaling to update the server session with the offer.
 *    3. Update call state (via redux actions).
 * @method offerInactiveMedia
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateSession "Update call" signalling function.
 * @param {Object}   action An action of type `CALL_HOLD`.
 */
export function * offerInactiveMedia (deps, action) {
  const requests = deps.requests

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Holding call.')

  const targetCall = yield select(getCallById, action.payload.id)
  // Validation: Call must exist and not be locally held already.
  if (!targetCall) {
    yield put(
      callActions.holdCallFinish(action.payload.id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call state not found; invalid call ID.'
        })
      })
    )
    return
  } else if (targetCall.localHold !== false) {
    yield put(
      callActions.holdCallFinish(action.payload.id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_STATE,
          message: 'Call is in an invalid state: localHold=true. It should be: localHold=false.'
        })
      })
    )
    return
  }

  const { wrtcsSessionId, webrtcSessionId, isAnonymous, account, customParameters, customBodies } = targetCall

  // TODO: Make sure the session is in the correct signaling state to start a
  //    renegotiation operation.
  const offer = yield call(
    generateOffer,
    deps,
    webrtcSessionId,
    {
      audio: 'inactive',
      video: 'inactive'
    },
    targetCall.bandwidth
  )

  if (!offer) {
    log.debug('Failed to generate SDP offer')
    yield put(
      callActions.holdCallFinish(action.payload.id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_OFFER,
          message: 'Failed to generate SDP offer'
        })
      })
    )
    return
  }

  const callInfo = {
    wrtcsSessionId,
    offer: offer.sdp,
    isAnonymous,
    account,
    customParameters,
    customBodies
  }

  const response = yield call(requests.updateSession, callInfo)

  if (response.error) {
    log.info('Failed to hold call.')

    // Rollback is not supported on plan-b
    const { defaultPeerConfig } = yield select(getOptions)
    if (defaultPeerConfig.sdpSemantics === 'unified-plan') {
      const result = yield call(
        rollbackOps.rollbackLocalHoldOperation,
        deps,
        webrtcSessionId,
        targetCall.localOp.operation
      )
      // There is a "browser issue" with Chrome where the remote track is removed from the call when the local description
      //  offer is set. This track is not recovered during rollback so call remains in an awkward state.
      //  See comments on PR-3224.
      const browser = deps.browserDetails().browser
      if (result.error || browser === 'chrome') {
        // Although Safari can automatically rollback and set another local offer while in `have-local-offer`
        //  signalling state, Safari does not perform an automatic rollback when a remote offer is
        //  received while in that state. An InvalidStateError is thrown instead and call is in an awkward state.
        if (browser === 'safari' || browser === 'chrome') {
          log.info('Ending call due to unrecoverable state after call hold failure.')
          // Hangup the call from WebRTC perspective
          yield call(closeCall, deps.webRTC, webrtcSessionId)

          // Cleanup Redux state by sending END_CALL_FINISH action
          yield put(
            callActions.endCallFinish(targetCall.id, {
              isLocal: true,
              transition: { reasonText: 'Call has ended due to call hold failure.' }
            })
          )
          return
        }
        log.info('Unable to reset state after hold operation failure, future operations may not work as intended.')
      }
    }

    yield put(
      callActions.holdCallFinish(action.payload.id, {
        local: true,
        error: response.error
      })
    )
  } else {
    log.info('Finished local portion of holding call. Waiting on remote response.')
    yield put(
      callActions.pendingOperation(action.payload.id, {
        operation: targetCall.localOp.operation
      })
    )
  }
}

/**
 * Updates an existing call to have "full" media.
 * Can be used as an "unhold" operation for plain webRTC scenarios.
 *
 * This saga defines how a call is taken off "hold". It performs the webRTC and
 *    signaling operations to update the local and server sessions of a call.
 *    This represents the start of the webRTC renegotiation process for the
 *    local side.
 * Assumptions:
 *    1. The call is in the correct state for the operation.
 *        - The call exists, and is locally held.
 * Responsibilities:
 *    1. Generate an offer with the media directions as "connected".
 *    2. Perform signaling to update the server session with the offer.
 *    3. Update call state (via redux actions).
 * @method offerFullMedia
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateSession "Update call" signalling function.
 * @param {Object}   action        A "unhold call" action.
 */
export function * offerFullMedia (deps, action) {
  const requests = deps.requests

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Unholding call.')

  const targetCall = yield select(getCallById, action.payload.id)
  // Validation: Call must exist and be locally held already.
  if (!targetCall) {
    yield put(
      callActions.unholdCallFinish(action.payload.id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call state not found; invalid call ID.'
        })
      })
    )
    return
  } else if (targetCall.localHold !== true) {
    yield put(
      callActions.unholdCallFinish(action.payload.id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_STATE,
          message: 'Call is in an invalid state: localHold=false. It should be: localHold=true.'
        })
      })
    )
    return
  }

  const { wrtcsSessionId, webrtcSessionId, isAnonymous, account, customParameters, customBodies } = targetCall

  // If the Session doesn't have a local track for a media kind, then don't change
  //    that direction to say we're sending one. This prevents the other side
  //    from getting an empty track.
  const sessionState = yield select(getSessionById, webrtcSessionId)
  const tracks = yield all(sessionState.localTracks.map(id => select(getTrackById, id)))
  const mediaDirections = {
    audio: tracks.some(track => track.kind === 'audio') ? 'sendrecv' : 'recvonly',
    video: tracks.some(track => track.kind === 'video') ? 'sendrecv' : 'recvonly'
  }

  // TODO: Make sure the session is in the correct signaling state to start a
  //    renegotiation operation.
  const offer = yield call(generateOffer, deps, webrtcSessionId, mediaDirections, targetCall.bandwidth)

  if (!offer) {
    log.debug('Invalid SDP offer or SDP offer not received.')
    yield put(
      callActions.unholdCallFinish(action.payload.id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_OFFER,
          message: 'Invalid SDP offer or SDP offer not received'
        })
      })
    )
    return
  }

  const callInfo = {
    wrtcsSessionId,
    offer: offer.sdp,
    isAnonymous,
    account,
    customParameters,
    customBodies
  }

  const response = yield call(requests.updateSession, callInfo)

  if (response.error) {
    log.info('Failed to unhold call.')

    // Rollback is not supported on plan-b
    const { defaultPeerConfig } = yield select(getOptions)
    if (defaultPeerConfig.sdpSemantics === 'unified-plan') {
      const result = yield call(
        rollbackOps.rollbackLocalUnholdOperation,
        deps,
        webrtcSessionId,
        targetCall.localOp.operation
      )
      if (result.error) {
        // Although Safari can automatically rollback and set another local offer while in `have-local-offer`
        //  signalling state, Safari does not perform an automatic rollback when a remote offer is
        //  received while in that state. An InvalidStateError is thrown instead and call is in an awkward state.
        if (deps.browserDetails().browser === 'safari') {
          log.info('Ending call due to unrecoverable state after call unhold failure.')
          // Hangup the call from WebRTC perspective
          yield call(closeCall, deps.webRTC, webrtcSessionId)

          // Cleanup Redux state by sending END_CALL_FINISH action
          yield put(
            callActions.endCallFinish(targetCall.id, {
              isLocal: true,
              transition: { reasonText: 'Call has ended due to call unhold failure.' }
            })
          )
          return
        }
        log.info('Unable to reset state after unhold operation failure, future operations may not work as intended.')
      }
    }

    yield put(
      callActions.unholdCallFinish(action.payload.id, {
        local: true,
        error: response.error
      })
    )
  } else {
    log.info('Finished local portion of unholding call. Waiting on remote response.')
    yield put(
      callActions.pendingOperation(action.payload.id, {
        operation: targetCall.localOp.operation
      })
    )
  }
}

/**
 * Sends custom parameters for a call.
 * @param {Object}   deps                                   Dependencies that the saga uses.
 * @param {Object}   deps.webRTC                            The WebRTC stack.
 * @param {Object}   deps.requests                          The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateCustomParameters   "Update call" signalling function.
 * @param {Object} action A "sent Custom Parameters" action.
 */
export function * sendCustomParameters (deps, action) {
  const requests = deps.requests

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Sending custom parameters for call.')

  // Get the call.
  const targetCall = yield select(getCallById, action.payload.id)

  // Validate call exists and call state is correct
  if (!targetCall) {
    log.debug(`Invalid Call ID. No call found with Call ID: ${action.payload.id}`)
    yield put(
      callActions.sendCustomParametersFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: `Invalid Call ID. No call found with Call ID: ${action.payload.id}`
        })
      })
    )
    return
  } else if (targetCall.state !== CALL_STATES.CONNECTED && targetCall.state !== CALL_STATES.ON_HOLD) {
    log.debug(`Call in invalid state for sending custom parameters. Call state: ${targetCall.state}`)
    yield put(
      callActions.sendCustomParametersFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_STATE,
          message: `Call in invalid state for sending custom parameters. Call state: ${targetCall.state}`
        })
      })
    )
    return
  }

  const { wrtcsSessionId, isAnonymous, account, customParameters } = targetCall

  const callInfo = {
    wrtcsSessionId,
    isAnonymous,
    account,
    customParameters
  }

  const response = yield call(requests.updateCustomParameters, callInfo)

  if (response.error) {
    log.info('Failed to send custom parameters.')
    yield put(
      callActions.sendCustomParametersFinish(action.payload.id, {
        error: response.error
      })
    )
  } else {
    log.info('Finished sending custom parameters.')
    yield put(
      callActions.sendCustomParametersFinish(action.payload.id, {
        error: false
      })
    )
  }
}

/**
 * Gets low-level statistics about a call (or track).
 *
 * This saga defines how/what statistics are retrieved about a call. It performs
 *    the webRTC operations on the local session. No signaling is involved.
 * The retrieved call statistics are a RTCStatsReport as provided by the WebRTC
 *    specification. Stats can be retrieved for either an entire call or
 *    individual tracks.
 * Assumptions:
 *    1. The call is on-going.
 * Responsibilities:
 *    1. Ensure the call exists.
 *    2. Use the webRTC layer to retrieve the stats.
 *    3. Dispatch an action with the results (which triggers an event).
 * @method getStats
 * @param {Object} deps        Dependencies that the saga uses.
 * @param {Object} deps.webRTC The WebRTC stack.
 * @param {Object} action      A "get Stats" action.
 */
export function * getStats (deps, action) {
  const { webRTC, browserDetails } = deps

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Getting call statistics.')

  // Get the call.
  const targetCall = yield select(getCallById, action.payload.id)
  // Validate call exists and call state is correct
  if (!targetCall) {
    const stateError = new BasicError({
      code: callCodes.INVALID_PARAM,
      message: 'Call state not found; invalid call ID.'
    })
    log.debug(`Invalid call state: ${stateError.message}`)

    yield call([action.meta.deferred, 'reject'], {
      error: stateError,
      trackId: action.payload.trackId
    })

    yield put(
      callActions.getStatsFinish(action.payload.id, {
        error: stateError,
        trackId: action.payload.trackId
      })
    )
    return
  }

  // Get the local Session for the call.
  const session = yield call([webRTC.sessionManager, 'get'], targetCall.webrtcSessionId)

  const trackId = action.payload.trackId
  // Retrieve the RTCStatsReport from the session.
  let rtcStatsReport
  try {
    rtcStatsReport = yield call([session, 'getStats'], trackId)
  } catch (error) {
    log.info('Failed to get call statistics.')
    const basicError = new BasicError({
      code: callCodes.GENERIC_ERROR,
      message: error.message
    })

    yield call([action.meta.deferred, 'reject'], {
      error: basicError
    })
    yield put(
      callActions.getStatsFinish(action.payload.id, {
        error: basicError,
        trackId
      })
    )
  }
  if (rtcStatsReport) {
    log.info('Finished getting call statistics.')

    // Extract values for our own custom Statistics
    const type = 'kandy_sdk_info'
    const id = 'kandy-sdk-info_' + sdkId
    const version = getVersion()
    const platform = yield select(getPlatform)

    let sdk
    if (platform === platforms.CPAAS) {
      sdk = '@kandy-io/cpaas-sdk'
    } else if (platform === platforms.UC) {
      sdk = '@kandy-io/uc-sdk'
    } else if (platform === platforms.LINK) {
      // callMe service also uses Link platform for call requests as well.
      if (targetCall.isAnonymous) {
        sdk = '@kandy-io/callme-sdk'
      } else {
        sdk = '@kandy-io/link-sdk'
      }
    }

    // Note that getting browser details is only intended for local browser.
    // For proxy mode this would return undefined. (see getBrowserDetails() on proxyStack)
    const details = browserDetails()

    // Define our custom Stats object
    const sdkMetaData = {
      id,
      type,
      sdk,
      version,
      callId: action.payload.id
    }

    if (details) {
      sdkMetaData.platform = details.browser + '/' + details.version
    }

    // Set the timestamp value of our custom report to be same value as
    // the timestamp associated with any stat coming from original webRTC report.
    const iter = rtcStatsReport.keys()
    const rtcStatValue = rtcStatsReport.get(iter.next().value)
    if (rtcStatValue) {
      sdkMetaData.timestamp = rtcStatValue.timestamp
    }
    // Add our custom stats to the ones reported by Web RTC.
    // Since rtcStatsReport seems to be read-only Map, create a new Map
    // which includes both our stat & the ones from webrtc.
    const result = new Map()
    result.set(id, sdkMetaData)
    rtcStatsReport.forEach(stat => {
      result.set(stat.id, stat)
    })
    yield call([action.meta.deferred, 'resolve'], result)

    yield put(callActions.getStatsFinish(action.payload.id, { result, trackId }))
  }
}

/**
 * Adds media to the call
 *
 * This saga performs the webRTC and signaling operations to
 *    update local and server sessions of a call.
 * Assumptions:
 *    1. Validate that the call state is "Connected".
 *        - The call is connected.
 * Responsibilities:
 *    1. Perform webRTC operations to add media.
 *    2. Update the backend with the new state.
 *    3. Update local state, using Redux
 * @method addMedia
 * @param {Object}   deps          Injected dependencies that addMedia relies on
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests requests that addMedia relies on
 * @param {Function} deps.requests.updateSession request to update the session on the backend
 * @param {Object}   action        An "add media" action.
 */
export function * addMedia (deps, action) {
  const requests = deps.requests
  const { bandwidth, mediaConstraints, dscpControls, id } = action.payload

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Adding media to call.')

  const targetCall = yield select(getCallById, id)
  // Validation: Call must exist and be Connected.
  if (!targetCall) {
    yield put(
      callActions.addMediaFinish(id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call state not found; invalid call ID.'
        })
      })
    )
    return
  } else if (targetCall.state !== CALL_STATES.CONNECTED) {
    yield put(
      callActions.addMediaFinish(id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_STATE,
          message: `Call is in an invalid state: state=${targetCall.state}. It should be: state=${CALL_STATES.CONNECTED}.`
        })
      })
    )
    return
  }

  // Get some call data.
  const {
    webrtcSessionId,
    wrtcsSessionId,
    bandwidth: callBandwidth,
    isAnonymous,
    account,
    localOp,
    customParameters,
    customBodies
  } = targetCall

  const finalBandwidth = {
    audio: bandwidth && bandwidth.audio ? bandwidth.audio : callBandwidth.audio,
    video: bandwidth && bandwidth.video ? bandwidth.video : callBandwidth.video
  }
  // Create media and add tracks using webRTC
  const { error, sdp, medias } = yield call(webRtcAddMedia, deps, mediaConstraints, {
    sessionId: webrtcSessionId,
    bandwidth: finalBandwidth,
    dscpControls
  })

  if (error) {
    yield put(
      callActions.addMediaFinish(id, {
        local: true,
        error: error
      })
    )
    return
  }

  const callInfo = {
    wrtcsSessionId,
    offer: sdp,
    isAnonymous,
    account,
    customParameters,
    customBodies
  }

  // Perform signalling to add media
  const response = yield call(requests.updateSession, callInfo)

  if (response.error) {
    log.info('Failed to add media to call.')
    // Rollback is not supported on safari or plan-b
    const { defaultPeerConfig } = yield select(getOptions)
    if (defaultPeerConfig.sdpSemantics === 'unified-plan') {
      const result = yield call(rollbackOps.rollbackLocalAddMedia, deps, webrtcSessionId, medias)
      const browser = deps.browserDetails().browser
      // There is an issue on Chrome where the remote track is removed from the call when the SDP rolls back
      //  leaving the call in an awkward state.
      if (result.error || browser === 'chrome') {
        // Although Safari can automatically rollback and set another local offer while in `have-local-offer`
        //  signalling state, Safari does not perform an automatic rollback when a remote offer is
        //  received while in that state. An InvalidStateError is thrown instead and call is in an awkward state.
        if (browser === 'safari' || browser === 'chrome') {
          log.info('Ending call due to unrecoverable state after call add media failure.')
          // Hangup the call from WebRTC perspective
          yield call(closeCall, deps.webRTC, webrtcSessionId)

          // Cleanup Redux state by sending END_CALL_FINISH action
          yield put(
            callActions.endCallFinish(id, {
              isLocal: true,
              transition: { reasonText: 'Call has ended due to call add media failure.' }
            })
          )
          return
        }
        log.info('Unable to reset state after add media operation failure, future operations may not work as intended.')
      }
    }

    yield put(
      callActions.addMediaFinish(id, {
        local: true,
        error: response.error
      })
    )
  } else {
    let tracks = []
    medias.forEach(media => {
      tracks = tracks.concat(media.tracks.map(track => track.id))
    })
    log.info('Finished local portion of adding media. Waiting on remote response.')
    yield put(
      callActions.pendingOperation(id, {
        operation: localOp.operation,
        operationData: {
          local: true,
          mediaIds: medias.map(media => media.id),
          tracks,
          bandwidth: finalBandwidth
        }
      })
    )
  }
}

/**
 *
 * Removes media from an existing session.
 *
 * This saga handles the WebRTC and signalling portions of removing media from an existing call
 * Assumptions:
 *    1. The action contains a tracks object that is neither an array or an empty object
 *    2. The call is in the 'Connected' state
 * Responsibilities:
 *    1. Perform the signaling to tell the server that we want to to remove the specified track
 *    2. Update call state (via redux actions).
 * @method removeMedia
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateSession "Update call" signalling function.
 * @param {Object}   action An action of type `REMOVE_MEDIA`.
 */
export function * removeMedia (deps, action) {
  const requests = deps.requests
  const { id, tracks, bandwidth } = action.payload

  const log = logManager.getLogger('CALL', id)
  log.info('Removing media from call.')

  // Handle scenario where no track ids are provided or not in an array.
  if (!isArray(tracks) || isEmpty(tracks)) {
    log.debug('No track ids specified to remove.')
    yield put(
      callActions.removeMediaFinish(id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'No track ids specified to remove.'
        })
      })
    )
    return
  }

  // Get some call data.
  const targetCall = yield select(getCallById, id)

  // Validation: Call must exist and be Connected.
  if (!targetCall) {
    yield put(
      callActions.removeMediaFinish(id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call state not found; invalid call ID.'
        })
      })
    )
    return
  } else if (targetCall.state !== CALL_STATES.CONNECTED) {
    yield put(
      callActions.removeMediaFinish(id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_STATE,
          message: `Call is in an invalid state: state=${targetCall.state}. It should be: state=${CALL_STATES.CONNECTED}.`
        })
      })
    )
    return
  }

  const {
    webrtcSessionId,
    wrtcsSessionId,
    bandwidth: callBandwidth,
    isAnonymous,
    account,
    localOp,
    customParameters,
    customBodies
  } = targetCall

  const finalBandwidth = {
    audio: bandwidth && bandwidth.audio ? bandwidth.audio : callBandwidth.audio,
    video: bandwidth && bandwidth.video ? bandwidth.video : callBandwidth.video
  }
  // Remove media and tracks using webRTC
  const { sdp, error } = yield call(webRtcRemoveMedia, deps, {
    sessionId: webrtcSessionId,
    tracks: tracks,
    bandwidth: finalBandwidth
  })
  if (error) {
    log.debug(`Failed to remove media: ${error.message}`)
    yield put(
      callActions.removeMediaFinish(id, {
        local: true,
        error
      })
    )
    return
  }

  const callInfo = {
    wrtcsSessionId,
    offer: sdp,
    isAnonymous,
    account,
    customParameters,
    customBodies
  }

  const response = yield call(requests.updateSession, callInfo)

  if (response.error) {
    log.info('Failed to remove media from call.')
    // Rollback is not supported on Safari or plan-b
    const { defaultPeerConfig } = yield select(getOptions)
    if (defaultPeerConfig.sdpSemantics === 'unified-plan') {
      const result = yield call(rollbackOps.rollbackLocalRemoveMedia, deps, webrtcSessionId)
      if (result.error) {
        // Although Safari can automatically rollback and set another local offer while in `have-local-offer`
        //  signalling state, Safari does not perform an automatic rollback when a remote offer is
        //  received while in that state. An InvalidStateError is thrown instead and call is in an awkward state.
        if (deps.browserDetails().browser === 'safari') {
          log.info('Ending call due to unrecoverable state after call hold failure.')
          // Hangup the call from WebRTC perspective
          yield call(closeCall, deps.webRTC, webrtcSessionId)

          // Cleanup Redux state by sending END_CALL_FINISH action
          yield put(
            callActions.endCallFinish(id, {
              isLocal: true,
              transition: { reasonText: 'Call has ended due to call hold failure.' }
            })
          )
          return
        }
        log.info('Unable to reset state after hold operation failure, future operations may not work as intended.')
      }
    }

    yield put(
      callActions.removeMediaFinish(id, {
        local: true,
        error: response.error
      })
    )
  } else {
    log.info('Finished local portion of removing media. Waiting on remote response.')
    yield put(
      callActions.pendingOperation(id, {
        operation: localOp.operation,
        operationData: {
          local: true,
          tracks: tracks,
          bandwidth: finalBandwidth
        }
      })
    )
  }
}

/**
 * Adds basic media scenario to the call
 *
 * This saga wraps addMedia as an easier to use function for simple scenarios
 * Assumptions:
 *    1. Validate that the call state is "Connected".
 *        - The call is connected.
 *    2. The track being added cannot already be on the call.
 * Responsibilities:
 *    1. Check that there isn't already a track of the same kind on the call
 *    2. Call addMedia with the correct parameters
 * @method addBasicMedia
 * @param {Object}   deps          Injected dependencies that addMedia relies on
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests requests that addMedia relies on
 * @param {Function} deps.requests.updateSession request to update the session on the backend
 * @param {Object}   action        An action of type `ADD_BASIC_MEDIA`.
 */
export function * addBasicMedia (deps, action) {
  const { id, kind } = action.payload

  const log = logManager.getLogger('CALL', id)
  log.debug(`Adding ${kind} media to call.`)

  const tracks = yield call(getLocalTracks, id, kind)
  if (tracks.length >= 1) {
    const message = `Too many ${kind} tracks for basic scenario!`
    log.debug(message)
    yield put(
      callActions.addMediaFinish(id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: message
        })
      })
    )
  } else {
    yield call(addMedia, deps, action)
  }
}

/**
 *
 * Removes basic media from an existing session.
 *
 * This saga wraps removeMedia as an easier to use function for simple scenarios
 * Assumptions:
 *    1. The call is in the 'Connected' state
 *    2. The track being removed should be the only one of its kind on the call.
 * Responsibilities:
 *    1. Check that this is a basic scenario (removing the only kind of track on the call)
 *    2. Call removeMedia with the correct parameters
 * @method removeMedia
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateSession "Update call" signalling function.
 * @param {Object}   action An action of type `REMOVE_BASIC_MEDIA`.
 */
export function * removeBasicMedia (deps, action) {
  const { id, kind } = action.payload

  const log = logManager.getLogger('CALL', id)
  log.debug(`Removing ${kind} media from call.`)

  const tracks = yield call(getLocalTracks, id, kind)
  if (tracks.length !== 1) {
    const message = `Must have only one ${kind} track for basic scenario!`
    log.debug(message)
    yield put(
      callActions.removeMediaFinish(id, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: message
        })
      })
    )
  } else {
    yield call(removeMedia, deps, {
      ...action,
      payload: {
        ...action.payload,
        tracks: tracks.map(trackObj => trackObj.trackId)
      }
    })
  }
}

/**
 *
 * Helper function to get all local tracks of a certain type
 *
 * @param {string} id the id of the call
 * @param {string} [kind] Kind must be of type 'audio' or 'video'
 * @return {Array<Object>} returns an array of tracks of the given type
 */
export function * getLocalTracks (id, kind) {
  const call = yield select(getCallById, id)
  const localTracks = call ? call.localTracks : []
  const tracks = yield all(localTracks.map(id => select(getTrackById, id)))
  return kind ? tracks.filter(track => track.kind === kind) : tracks
}

/**
 *
 * Direct Transfers an ongoing call.
 *
 * This saga handles the WebRTC and signalling portions of direct transferring an ongoing call
 * Assumptions:
 *    1. The action contains a destination address
 *    2. The call is in the 'On Hold' state
 * Responsibilities:
 *    1. Perform the signaling to tell the server that we want to direct transfer the call to a specified destination address.
 *    2. Update call state (via redux actions).
 * @method directTransfer
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.directTransferSession "Direct transfer" signalling function.
 * @param {Object}   action An action of type `DIRECT_TRANSFER`.
 */
export function * directTransfer (deps, action) {
  const { requests } = deps

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Performing direct transfer on call.')

  const currentCall = yield select(getCallById, action.payload.id)

  // Validation: Call must exist and be On Hold.
  if (!currentCall) {
    log.debug('Cannot direct transfer call: Invalid call ID.')
    yield put(
      callActions.directTransferFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call state not found; invalid call ID.'
        })
      })
    )
    return
  } else if (currentCall.state !== CALL_STATES.ON_HOLD) {
    log.debug('Cannot direct transfer call: Call in invalid state.')
    yield put(
      callActions.directTransferFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_STATE,
          message: `Call is in an invalid state: state=${currentCall.state}. It should be: state=${CALL_STATES.ON_HOLD}.`
        })
      })
    )
    return
  }

  // Collect the information needed to make the request.
  const callInfo = {
    wrtcsSessionId: currentCall.wrtcsSessionId,
    address: action.payload.destination
  }
  const response = yield call(requests.directTransferSession, callInfo)

  if (response.error) {
    log.info('Failed to direct transfer call.')
    yield put(
      callActions.directTransferFinish(action.payload.id, {
        error: response.error
      })
    )
    return
  }

  log.info('Finished local portion of direct transfer. Waiting on remote response.')
  yield put(
    callActions.pendingOperation(action.payload.id, {
      operation: currentCall.localOp.operation
    })
  )
}

/**
 *
 * Transfers 2 ongoing calls to one another.
 *
 * This saga handles the WebRTC and signalling portions of transferring 2 ongoing calls to one another.
 * Assumptions:
 *    1. The action contains the other call id
 *    2. The calls are in the 'On Hold' state
 * Responsibilities:
 *    1. Perform the signaling to tell the server that we want to transfer 2 ongoing calls to one other.
 *    2. Update call state (via redux actions).
 * @method consultativeTransfer
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.consultativeTransferSessions "Consultative transfer" signalling function.
 * @param {Object}   action An action of type `CONSULTATIVE_TRANSFER`.
 */
export function * consultativeTransfer (deps, action) {
  const { requests } = deps

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Performing consultative transfer on call.')

  const currentCall = yield select(getCallById, action.payload.id)
  const otherCall = yield select(getCallById, action.payload.otherCallId)

  // Validation: Calls must exist and be On Hold.
  if (!currentCall || !otherCall) {
    const missingId = currentCall ? action.payload.otherCallId : action.payload.id
    log.debug(`Cannot transfer call ${missingId}: Invalid call ID.`)
    yield put(
      callActions.consultativeTransferFinish(action.payload.id, {
        otherCallId: action.payload.otherCallId,
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call state not found; invalid call ID.'
        })
      })
    )
    return
  } else if (currentCall.state !== CALL_STATES.ON_HOLD || otherCall.state !== CALL_STATES.ON_HOLD) {
    const invalidCall = currentCall.state === CALL_STATES.ON_HOLD ? otherCall : currentCall
    log.debug(`Cannot transfer call ${invalidCall.id}: Call in invalid state.`)
    yield put(
      callActions.consultativeTransferFinish(action.payload.id, {
        otherCallId: action.payload.otherCallId,
        error: new BasicError({
          code: callCodes.INVALID_STATE,
          message: `Call is in an invalid state: state=${invalidCall.state}. It should be: state=${CALL_STATES.ON_HOLD}.`
        })
      })
    )
    return
  }

  // Collect the information needed to make the request.
  const callInfo = {
    wrtcsSessionId: currentCall.wrtcsSessionId,
    otherWrtcsSessionId: otherCall.wrtcsSessionId,
    destination: otherCall.direction === 'outgoing' ? otherCall.to : otherCall.from
  }
  const response = yield call(requests.consultativeTransferSessions, callInfo)

  if (response.error) {
    log.info('Failed to consultative transfer call.')
    yield put(
      callActions.consultativeTransferFinish(action.payload.id, {
        error: response.error,
        otherCallId: action.payload.otherCallId
      })
    )
    return
  }

  log.info('Finished local portion of consultative transfer. Waiting on remote response.')
  yield put(callActions.pendingConsultativeTransfer(currentCall.id, { otherCallId: otherCall.id }))
}

/**
 *
 * Joins 2 ongoing calls.
 *
 * This saga handles the WebRTC and signalling portions of joining 2 ongoing calls
 * Assumptions:
 *    1. The action contains the other call id
 *    2. The calls are in the 'On Hold' state
 * Responsibilities:
 *    1. Perform the signaling to tell the server that we want to join 2 ongoing calls.
 *    2. Update call state (via redux actions).
 * @method join
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.joinSessions "Join" signalling function.
 * @param {Object}   action An action of type `JOIN`.
 */
export function * join (deps, action) {
  const requests = deps.requests

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Performing join on call.')

  const currentCall = yield select(getCallById, action.payload.id)
  const otherCall = yield select(getCallById, action.payload.otherCallId)

  // Validation: Calls must exist and be On Hold.
  if (!currentCall || !otherCall) {
    const missingId = currentCall ? action.payload.otherCallId : action.payload.id
    log.debug(`Cannot join call ${missingId}: Invalid call ID.`)
    yield put(
      callActions.joinFinish(action.payload.id, {
        usedCallIds: [action.payload.id, action.payload.otherCallId],
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call state not found; invalid call ID.'
        })
      })
    )
    return
  } else if (currentCall.state !== CALL_STATES.ON_HOLD || otherCall.state !== CALL_STATES.ON_HOLD) {
    const invalidCall = currentCall.state === CALL_STATES.ON_HOLD ? otherCall : currentCall
    log.debug(`Cannot join call ${invalidCall.id}: Call in invalid state.`)
    yield put(
      callActions.joinFinish(action.payload.id, {
        usedCallIds: [action.payload.id, action.payload.otherCallId],
        error: new BasicError({
          code: callCodes.INVALID_STATE,
          message: `Call is in an invalid state: state=${invalidCall.state}. It should be: state=${CALL_STATES.ON_HOLD}.`
        })
      })
    )
    return
  }

  // Determine combined participant name if any
  const currentCallRemoteName = currentCall.remoteParticipant.displayName
  const otherCallRemoteName = otherCall.remoteParticipant.displayName
  let participantName
  if (currentCallRemoteName && otherCallRemoteName) {
    participantName = `${currentCallRemoteName},${otherCallRemoteName}`
  } else if (currentCallRemoteName) {
    participantName = `${currentCallRemoteName},`
  } else if (otherCallRemoteName) {
    participantName = `,${otherCallRemoteName}`
  }

  // Determine combined remote participant address
  const currentCallRemoteAddress = currentCall.direction === CALL_DIRECTION.OUTGOING ? currentCall.to : currentCall.from
  const otherCallRemoteAddress = otherCall.direction === CALL_DIRECTION.OUTGOING ? otherCall.to : otherCall.from
  const participantAddress = `${currentCallRemoteAddress},${otherCallRemoteAddress}`

  // User the audio constraints from the current call and set video to false.
  const mediaConstraints = {
    video: false,
    audio: currentCall.mediaConstraints ? currentCall.mediaConstraints.audio || true : true
  }

  // Use the bandwidth constraints from the current call
  const bandwidth = checkBandwidthControls(currentCall.bandwidth)

  const dscpControls = currentCall.dscpControls

  const turnInfo = yield select(getTurnInfo)
  const { trickleIceMode, defaultPeerConfig, removeBundling, serverTurnCredentials } = yield select(getOptions)

  // Create the new joined call in state right away
  yield put(
    callActions.joinStart(action.payload.newCallId, {
      // The media constraints this call has.
      mediaConstraints,
      // The combined addresses of the 2 other participants in the joined call.
      participantAddress,
      // The combined names of the 2 other participants in the joined call.
      participantName,
      // This call's current user
      from: action.payload.from,
      // The ids of the calls that were used for joining.
      usedCallIds: [currentCall.id, otherCall.id],
      // The custom parameters of the combined call.
      customParameters: currentCall.customParameters,
      // The DSCP Controls of the combined call.
      dscpControls,
      // The bandwidth from the original call.
      bandwidth
    })
  )

  const { error, offerSdp, sessionId, mediaIds } = yield call(setupCall, deps, mediaConstraints, {
    defaultPeerConfig,
    turnInfo,
    trickleIceMode,
    dscpControls,
    removeBundling,
    bandwidth,
    callId: action.payload.newCallId,
    serverTurnCredentials
  })

  // An error occurred while trying to setup the WebRTC portion of the joined call.
  //    Report the error and mark the call as ended.
  if (error) {
    log.info(`Failed to initiate call. Changing to ${CALL_STATES.ENDED}.`)
    yield put(
      callActions.joinFinish(action.payload.newCallId, {
        usedCallIds: [currentCall.id, otherCall.id],
        error: error
      })
    )
    return
  }

  // Collect the information needed to make the request.
  const callInfo = {
    wrtcsSessionId: currentCall.wrtcsSessionId,
    otherWrtcsSessionId: otherCall.wrtcsSessionId,
    sdp: offerSdp,
    // Use the customParameters of the current call
    customParameters: currentCall.customParameters
  }
  const response = yield call(requests.joinSessions, callInfo)

  if (response.error) {
    log.info('Failed to join call.')
    yield put(
      callActions.joinFinish(action.payload.newCallId, {
        error: response.error,
        usedCallIds: [action.payload.id, action.payload.otherCallId]
      })
    )
    return
  }

  log.info('Finished local portion of join. Waiting on remote response.')
  // Dispatch an action to do the following:
  //  - create a new "joined" call in state
  //  - update calls used in the join to have isPending property
  yield put(
    callActions.pendingJoin(action.payload.newCallId, {
      // The ID that the backend uses to track this webRTC session.
      wrtcsSessionId: response.newWrtcsSessionId,
      // The ID that the webRTC stack uses to track this webRTC session.
      webrtcSessionId: sessionId,
      // The local Media object associated with this call.
      mediaIds,
      // The ids of the calls that were used for joining.
      usedCallIds: [currentCall.id, otherCall.id],
      // Set the state of the joined call to Initiated.
      state: CALL_STATES.INITIATED
    })
  )
}

/**
 *
 * Replaces an existing track with a new track.
 *
 * This saga handles the WebRTC portion of replacing a track.
 * Assumptions:
 *    1. The action contains call id, trackId, and mediaConstraints
 * Responsibilities:
 *    1. Creates a new track.
 *    2. Replaces an an existing track with the newly created track.
 *    3. Update call state (via redux actions).
 * @method replaceTrack
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   action An action of type `REPLACE_TRACK`.
 */
export function * replaceTrack (deps, action) {
  const { webRTC } = deps

  const log = logManager.getLogger('CALL', action.payload.id)
  log.info('Replacing track on call.')

  const { trackId, mediaConstraints } = action.payload

  const currentCall = yield select(getCallById, action.payload.id)
  if (!currentCall) {
    log.debug(`Call ${action.payload.id} not found.`)
    yield put(
      callActions.replaceTrackFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_PARAM,
          message: 'Call state not found; invalid call ID.'
        })
      })
    )
    return
  }

  if ([CALL_STATES.CANCELLED, CALL_STATES.ENDED, CALL_STATES.INITIATING].includes(currentCall.state)) {
    log.debug(`Invalid call state: ${currentCall.state}`)
    yield put(
      callActions.replaceTrackFinish(action.payload.id, {
        error: new BasicError({
          code: callCodes.INVALID_STATE,
          message: `Call is in an invalid state: ${currentCall.state}.`
        })
      })
    )
    return
  }

  const { error, newTrackId, oldTrackState } = yield call(webRtcReplaceTrack, webRTC, {
    sessionId: currentCall.webrtcSessionId,
    trackId,
    mediaConstraints
  })

  if (error) {
    log.info('Failed to replace track on call.')
    yield put(callActions.replaceTrackFinish(action.payload.id, { error }))
  } else {
    log.info('Finished replacing track on call.')
    yield put(callActions.replaceTrackFinish(action.payload.id, { newTrackId, oldTrackState }))
  }
}

/**
 * Updates an existing call with unchanged media to trigger an ICE restart and renegotiation.
 *
 * This saga defines how a call's media is restarted. It performs the webRTC and
 *    signaling operations to update the local and server sessions of a call.
 *    This represents the start of the webRTC renegotiation process for the
 *    local side.
 * Assumptions:
 *    1. The call is in the correct state for the operation.
 *        - The call exists.
 * Responsibilities:
 *    1. Flag the Peer to restart ICE.
 *    2. Generate an offer with unchanged media directions.
 *    3. Perform signaling to update the server session with the offer.
 *    4. Update call state (via redux actions).
 * @method iceRestart
 * @param {Object}   deps          Dependencies that the saga uses.
 * @param {Object}   deps.webRTC   The WebRTC stack.
 * @param {Object}   deps.requests The set of platform-specific signalling functions.
 * @param {Function} deps.requests.updateSession "Update call" signalling function.
 * @param {Object}   action An action of type `ICE_RESTART`.
 */
export function * iceRestart (deps, action) {
  const requests = deps.requests
  const { id: callId } = action.payload
  // Get some call data.
  const {
    webrtcSessionId,
    wrtcsSessionId,
    bandwidth,
    isAnonymous,
    account,
    localOp,
    localHold,
    customParameters,
    customBodies
  } = yield select(getCallById, callId)

  const session = yield call([deps.webRTC.sessionManager, 'get'], webrtcSessionId)

  const dtlsRole = yield call([session, 'getDtlsRole'])

  // The passive side of the call should wait a short amount of time before restarting media connection
  // to avoid a glare scenario where both sides of the call try to restart the media connection
  // at the same time.
  if (dtlsRole === 'passive') {
    const { mediaConnectionRetryDelay } = yield select(getOptions)

    yield delay(mediaConnectionRetryDelay)
  }

  // Tell the RTCPeerConnection to restart the ICE connection as part of the
  //    next negotiation.
  yield call([session, 'restartIce'])

  const log = logManager.getLogger('CALL', callId)

  // Before doing the negotiation for ICE Restart, make sure we have network.
  const platform = yield select(getPlatform)
  const { connected: isConnected } = yield select(getConnectionState, platform)

  if (!isConnected) {
    log.debug('Websocket not connected; waiting for reconnect before performing media restart.')
    const isConnected = yield call(waitForReconnect)

    if (!isConnected) {
      log.info('Subscription lost; stopping media restart attempt.')
      yield put(
        callActions.restartIceFinish(callId, {
          local: true,
          error: new BasicError({
            code: subscriptionCodes.WS_CONNECTION_ERROR,
            message: 'No subscription for operation.'
          })
        })
      )
      return
    }
  }

  log.info('Performing call renegotiation to reconnect media connection.')

  // Make we have the right media directions in the negotiation.
  //    We don't want to accidentally unhold media while on local hold.
  const mediaDirections = {
    audio: localHold ? 'inactive' : 'sendrecv',
    video: localHold ? 'inactive' : 'sendrecv'
  }
  // If we're not on hold nor sending any audio or video, change the media direction(s) to recvonly to
  //  prevent the other side from getting empty tracks.
  if (!localHold) {
    const localTracks = yield call(getLocalTracks, callId)
    mediaDirections.audio = localTracks.some(track => track.kind === 'audio') ? 'sendrecv' : 'recvonly'
    mediaDirections.video = localTracks.some(track => track.kind === 'video') ? 'sendrecv' : 'recvonly'
  }
  const offer = yield call(generateOffer, deps, webrtcSessionId, mediaDirections, bandwidth)
  if (!offer) {
    log.debug('Failed to generate offer.')
    yield put(
      callActions.restartMediaFinish(callId, {
        local: true,
        error: new BasicError({
          code: callCodes.INVALID_OFFER,
          message: 'Failed to generate SDP offer'
        })
      })
    )
    return
  }

  const callInfo = {
    wrtcsSessionId,
    offer: offer.sdp,
    isAnonymous,
    account,
    customParameters,
    customBodies
  }

  const response = yield call(requests.updateSession, callInfo)

  if (response.error) {
    log.info('Failed to renegotiate call.')
    yield put(
      callActions.restartMediaFinish(callId, {
        local: true,
        error: response.error
      })
    )
  } else {
    log.info('Finished local portion of media restart. Waiting on remote response.')
    yield put(
      callActions.pendingOperation(callId, {
        operation: localOp.operation
      })
    )
  }
}
